import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { RuntimeConfigLoaderService } from 'runtime-config-loader';

/**
 * PrintConnectService
 *
 * All communication with the local Print Connect machine lives here and only
 * here. Components call the semantic methods and never do raw fetch/socket
 * against the printer, and never need to know how the physical printer is
 * wired (Bluetooth / USB / network) — that detail is entirely inside
 * Print Connect.
 *
 * The iPad talks to Print Connect over plain HTTP on the local network:
 *
 *   Pick Station (browser)  --HTTP-->  Print Connect  --Bluetooth-->  printer
 *
 * The base URL is configurable (never hard-coded). Resolution order:
 *   1. runtime config apiServer.printConnectUrl      (e.g. http://print-connect.local:8899)
 *   2. window location host swap + configurable port (e.g. http://<same-host>:8899)
 *   3. default http://print-connect.local:8899
 */
@Injectable({
	providedIn: 'root'
})
export class PrintConnectService {
	constructor(
		private http: HttpClient,
		private _configSvc: RuntimeConfigLoaderService,
	) {}

	get baseUrl(): string {
		try {
			const cfg: any = this._configSvc.getConfig();
			if (cfg && cfg.apiServer) {
				if (cfg.apiServer.printConnectUrl) {
					return String(cfg.apiServer.printConnectUrl).replace(/\/+$/, '');
				}
				if (cfg.apiServer.baseUrl && typeof window !== 'undefined') {
					return window.location.origin + ':8899';
				}
			}
		} catch (e) { /* ignore */ }
		return 'http://print-connect.local:8899';
	}

	get endpoint(): string {
		return this.baseUrl;
	}

	private headers(): HttpHeaders {
		return new HttpHeaders({ 'Content-Type': 'application/json' });
	}

	/** Print a pick label via the local Print Connect machine. */
	printPickLabel(payload: { printer: string; jobType: string; data: any }) {
		return this.http.post<any>(this.endpoint + '/api/print', {
			printer: payload.printer,
			jobType: payload.jobType,
			kind: 'slipUrl',
			data: payload.data,
		}, { headers: this.headers() });
	}

	/** Lightweight availability probe. */
	isAvailable() {
		return this.http.get<any>(this.endpoint + '/api/status', { headers: this.headers() });
	}

	getStatus() {
		return this.isAvailable();
	}
}
